<template>
  <div class="p-3 flex flex-col bg-key rounded-4xl">
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-1 h-9 text-black-white text-l-medium">
        <Icon icon="Function-Wireframe-Clock" class="size-6" />
        <span class="text-l-medium text-black-white">{{
          t('setting.device.wirelessSleepTime.title')
        }}</span>
      </div>
      <div class="flex-1" />
    </div>
    <div>
      <p class="text-m-medium text-text-glass-default">
        {{ t('setting.device.wirelessSleepTime.description', { device: '键盘' }) }}
      </p>
    </div>
    <div class="mt-1 pt-6 pb-4 w-full flex items-center justify-between">
      <div class="w-full flex items-center gap-4 text-m-medium">
        <span class="text-text-glass-default">
          {{ t('setting.device.wirelessSleepTime.shortTimeLabel') }}
        </span>
        <div class="relative flex-1">
          <Slider
            v-model="currentSleepTime"
            :min="minValue"
            :max="maxValue"
            :step="1"
            thumb-alignment="overflow"
            class="w-full"
            track-class="h-3 bg-neutral-light-default"
            range-class="bg-brand-blue1"
            thumb-class="size-4.5 bg-blue-dark-default"
            @update:model-value="updateSleepTime"
          />
          <div
            class="absolute bottom-4 left-0 -translate-x-1/2 pointer-events-none text-center leading-none whitespace-nowrap"
            :style="{ left: bubbleLeft }"
          >
            <div class="text-m-medium text-brand-blue1">
              {{ currentSleepTime[0] }}{{ t('setting.device.wirelessSleepTime.minuteUnit') }}
            </div>
          </div>
        </div>
        <span class="text-text-glass-default">
          {{ t('setting.device.wirelessSleepTime.longTimeLabel') }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue'
  import { Icon, Slider } from '@rapoo/element'
  import { useLocaleStore } from '@/stores'
  import { useDeviceStore } from '@/stores'
  import { storeToRefs } from 'pinia'
  import type { KeyboardPerfCapability } from '@rapoo/device-sdk'

  const { t } = useLocaleStore()
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)

  const perfCap = computed(() => {
    const cap = currentDevice.value?.manifest.capabilities?.find(
      (c): c is KeyboardPerfCapability => c.type === 'keyboardPerf',
    )
    return cap?.config
  })
  const minValue = computed(() => perfCap.value?.sleepTimeRange?.min ?? 2)
  const maxValue = computed(() => perfCap.value?.sleepTimeRange?.max ?? 120)

  // 实时跟踪拖动位置（bubble 跟随用）
  const currentSleepTime = ref([30])

  function requirePerfCfg() {
    const perf = currentDevice.value?.features?.keyboardPerfCfg
    if (!perf) throw new Error('当前设备未装配性能参数能力')
    return perf
  }

  onMounted(async () => {
    try {
      const time = await requirePerfCfg().getSleepTime()
      currentSleepTime.value = [time > 0 ? time : 30]
    } catch {
      // 默认值
    }
  })

  // 监听滑块变化写入设备
  const updateSleepTime = async (val: number[] | undefined) => {
    if (!val) return
    currentSleepTime.value = val
    const time = val[0]
    if (time >= 2 && time <= 120) {
      await requirePerfCfg().setSleepTime(time)
    }
  }

  const bubbleLeft = computed(() => {
    const ratio = (currentSleepTime.value[0] - minValue.value) / (maxValue.value - minValue.value)
    const progress = Math.min(1, Math.max(0, ratio))
    return `${progress * 100}%`
  })
</script>
